import { FunctionComponent, useCallback } from "react"
import { AircraftManualStatus } from "@nova/schemas"
import { useAircraftNickname, useUpdateAircraft, useUpdateAircraftStatus } from "./AircraftQueries"
import { AircraftFormModal, AircraftFormValues } from "./AircraftFormModal"
import { useAircraftMaintenanceStatus } from "./AircraftStatusQueries"
import { useConfirmationModalContext } from "features/modals/WorkspaceConfirmationModalV2"
import { DeleteAircraftModal } from "./DeleteAircraftModal"
import { useRouter } from "pages"
import { useMaintenanceTemplatesForAircraft } from "../maintenance/MaintenanceTemplateQueries"

const toDateInputValue = (date: Date | null | undefined): string => {
    if (!date) return ""
    return new Date(date).toISOString().split("T")[0]
}

interface EditAircraftModalProps {
    aircraftId: string
    initialValues: {
        nickname: string | null
        manufacturer: string | null
        model: string | null
        serial_number: string
        registration_number: string | null
        purchased_at: Date | null
        warranty_expires_at: Date | null
        notes: string | null
        manual_status: AircraftManualStatus
    }
    onSubmit: () => void
    onCancel: () => void
}

const EditAircraftModal: FunctionComponent<EditAircraftModalProps> = ({
    aircraftId,
    initialValues,
    onCancel,
    onSubmit,
}) => {
    const router = useRouter("/workspace/:workspaceId")
    const { updateAircraft, isUpdating: isUpdatingAircraft } = useUpdateAircraft()
    const { updateAircraftStatus, isUpdating: isUpdatingStatus } = useUpdateAircraftStatus()
    const { templates } = useMaintenanceTemplatesForAircraft(aircraftId)
    const { status: computedStatus } = useAircraftMaintenanceStatus(
        aircraftId,
        initialValues.manual_status
    )
    const nickname = useAircraftNickname(aircraftId)

    const handleSubmit = async (values: AircraftFormValues) => {
        await updateAircraft({
            aircraftId,
            data: {
                nickname: values.nickname || null,
                manufacturer: values.manufacturer || undefined,
                model: values.model || null,
                serial_number: values.serialNumber,
                registration_number: values.registrationNumber || null,
                purchased_at: values.purchasedAt || null,
                warranty_expires_at: values.warrantyExpiresAt || null,
                notes: values.notes || null,
            },
        })
        if (values.manualStatus !== computedStatus && values.manualStatus !== "maintenance_due") {
            await updateAircraftStatus({
                aircraftId,
                status: values.manualStatus,
                templateIds:
                    computedStatus === "maintenance_due" && values.manualStatus === "good_to_fly"
                        ? templates.map((t) => t.id)
                        : [],
            })
        }
        onSubmit()
    }

    const confirmationModal = useConfirmationModalContext()
    const handleDelete = useCallback(async () => {
        const didConfirm = await confirmationModal.confirm({
            RenderComponent: ({ cancel, confirm }) => (
                <DeleteAircraftModal
                    aircraftId={aircraftId}
                    nickname={nickname}
                    onCancel={cancel}
                    onSubmit={confirm}
                />
            ),
        })
        if (didConfirm) {
            router.navigate({ path: "/fleet/aircraft" })
        }
    }, [aircraftId, confirmationModal, nickname, router])

    if (computedStatus === null) return null

    return (
        <AircraftFormModal
            title="Edit Drone"
            submitLabel="Save Changes"
            initialValues={{
                nickname: initialValues.nickname ?? "",
                manufacturer: initialValues.manufacturer ?? undefined,
                model: initialValues.model ?? undefined,
                serialNumber: initialValues.serial_number,
                registrationNumber: initialValues.registration_number ?? "",
                purchasedAt: toDateInputValue(initialValues.purchased_at),
                warrantyExpiresAt: toDateInputValue(initialValues.warranty_expires_at),
                notes: initialValues.notes ?? "",
                manualStatus:
                    computedStatus === "maintenance_due" ? "needs_maintenance" : computedStatus,
            }}
            isMutating={isUpdatingAircraft || isUpdatingStatus}
            onSubmit={handleSubmit}
            onDelete={handleDelete}
            onCancel={onCancel}
        />
    )
}

export { EditAircraftModal }
